import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { 
  FileEdit, Search, Eye, Plus, Trash2, Save, RefreshCw, Send,
  Briefcase, GraduationCap, MapPin, CreditCard, Users, 
  Building2, Phone, Mail, Calendar, CheckCircle, XCircle,
  Upload, FileText, File, X, Download, Image, User, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { formatPhoneMax10, formatDateForDisplay } from '@/utils/textFormatting';

const INDIAN_STATES = [
  'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
  'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
  'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
  'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
  'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
  'Delhi', 'Chandigarh'
];

const EDUCATION_TYPES = [
  '10th / SSLC', '12th / PUC / HSC', 'ITI', 'Diploma', 'Graduation', 
  'Post Graduation', 'Doctorate', 'Professional Certification', 'Others'
];

const RESIDENCE_TYPES = ['Own', 'Rented', 'Parental', 'Company Provided', 'PG/Hostel'];

const GAP_REASONS = [
  'Higher Studies',
  'Holiday/Vacation Break',
  'Marriage',
  'Family Responsibilities',
  'Health Issues',
  'Career Break',
  'Job Search',
  'Relocation',
  'Personal Development/Training',
  'Freelancing/Consulting',
  'Starting Own Business',
  'Maternity/Paternity Leave',
  'Travel',
  'Other'
];

export default function CheckDetailsEntry({ initialCase = null, onClearInitialCase = null }) {
  const [loading, setLoading] = useState(false);
  const [cases, setCases] = useState([]);
  const [selectedCase, setSelectedCase] = useState(null);
  const [employeeData, setEmployeeData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('basic');
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [initialCaseLoaded, setInitialCaseLoaded] = useState(false);
  
  // Fewer-records declaration checkboxes (per-check-type) — used in Review & Submit
  const [employmentDeclaration, setEmploymentDeclaration] = useState(false);
  const [educationDeclaration, setEducationDeclaration] = useState(false);
  const [addressDeclaration, setAddressDeclaration] = useState(false);

  // Form data states
  const [employmentEntries, setEmploymentEntries] = useState([]);
  const [educationEntries, setEducationEntries] = useState([]);
  const [addressData, setAddressData] = useState({
    present: { house_no: '', cross: '', main: '', area: '', city: '', state: '', pincode: '', landmark: '', residence_type: '', period_of_stay: '' },
    permanent: { house_no: '', cross: '', main: '', area: '', city: '', state: '', pincode: '', landmark: '', residence_type: '', period_of_stay: '' },
    same_as_present: false,
    proof_type: '',
    proof_document: null,
    proof_document_url: '',
    proof_reference: ''
  });
  const [additionalAddresses, setAdditionalAddresses] = useState([]);
  const [idData, setIdData] = useState({ pan_number: '', aadhar_number: '', dl_number: '', passport_number: '' });
  const [referencesData, setReferencesData] = useState({ personal: [], professional: [] });
  const [documents, setDocuments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Additional Address Functions
  const addAdditionalAddress = () => {
    setAdditionalAddresses([...additionalAddresses, {
      address_type: 'previous',
      house_no: '',
      cross: '',
      main: '',
      area: '',
      city: '',
      state: '',
      pincode: '',
      landmark: '',
      residence_type: '',
      period_of_stay: '',
      duration_from: '',
      duration_to: ''
    }]);
  };

  const removeAdditionalAddress = (index) => {
    setAdditionalAddresses(additionalAddresses.filter((_, i) => i !== index));
  };

  const updateAdditionalAddress = (index, field, value) => {
    const updated = [...additionalAddresses];
    updated[index][field] = value;
    setAdditionalAddresses(updated);
  };

  const DOCUMENT_TYPES = [
    { value: 'resume', label: 'Resume / CV' },
    { value: 'offer_letter', label: 'Offer Letter' },
    { value: 'experience_letter', label: 'Experience / Relieving Letter' },
    { value: 'payslip', label: 'Payslip' },
    { value: 'form16', label: 'Form 16' },
    { value: 'education_certificate', label: 'Education Certificate' },
    { value: 'marksheet', label: 'Marksheet' },
    { value: 'degree_certificate', label: 'Degree Certificate' },
    { value: 'pan_card', label: 'PAN Card' },
    { value: 'aadhar_card', label: 'Aadhar Card' },
    { value: 'driving_license', label: 'Driving License' },
    { value: 'passport', label: 'Passport' },
    { value: 'voter_id', label: 'Voter ID' },
    { value: 'address_proof', label: 'Address Proof' },
    { value: 'bank_statement', label: 'Bank Statement' },
    { value: 'reference_letter', label: 'Reference Letter' },
    { value: 'other', label: 'Other Document' }
  ];

  useEffect(() => {
    fetchCases();
  }, []);

  // Auto-load initial case when provided (e.g., from "Edit Check Details" button)
  useEffect(() => {
    if (initialCase && !initialCaseLoaded && cases.length > 0) {
      // Find the case in our loaded cases list
      const caseToLoad = cases.find(c => c.id === initialCase.id);
      if (caseToLoad) {
        loadCaseDetails(caseToLoad);
        setInitialCaseLoaded(true);
        // Clear the initial case prop so it doesn't reload on re-render
        if (onClearInitialCase) {
          onClearInitialCase();
        }
      } else {
        // Case not found in filtered list, try to load directly
        loadCaseDetails(initialCase);
        setInitialCaseLoaded(true);
        if (onClearInitialCase) {
          onClearInitialCase();
        }
      }
    }
  }, [initialCase, cases, initialCaseLoaded]);

  // Reset the loaded flag when component unmounts or initialCase changes
  useEffect(() => {
    if (!initialCase) {
      setInitialCaseLoaded(false);
    }
  }, [initialCase]);

  const fetchCases = async () => {
    setLoading(true);
    try {
      // Fetch cases without status filter to get all cases accessible to this user
      // Only show draft/pending cases that still need data entry (not yet approved by QC)
      const response = await axios.get('/cases');
      const allCases = response.data || [];
      const filteredCases = allCases.filter(c => 
        ['initiated', 'basic_initiated'].includes(c.status)
      ).sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      setCases(filteredCases);
    } catch (error) {
      console.error('Failed to fetch cases:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCaseDetails = async (caseItem) => {
    if (!caseItem) {
      console.error('No case item provided');
      return;
    }
    
    // Validate employee_id exists
    if (!caseItem.employee_id) {
      console.error('Case item missing employee_id:', caseItem);
      toast.error('Unable to load case - missing employee information');
      return;
    }
    
    try {
      // If caseItem doesn't have checks (e.g., passed from Candidate Drafts), fetch full case data
      let fullCase = caseItem;
      if (!caseItem.checks && caseItem.id) {
        try {
          const caseResponse = await axios.get(`/cases/${caseItem.id}`);
          if (caseResponse.data) {
            fullCase = caseResponse.data;
          }
        } catch (err) {
          console.warn('Could not fetch full case data, using partial:', err);
        }
      }
      
      const response = await axios.get(`/employees/${fullCase.employee_id || caseItem.employee_id}/full-details`);
      const employee = response.data.employee || response.data;
      
      setSelectedCase(fullCase);
      setEmployeeData(employee);
      
      // Load existing data into forms
      // Employment: use employment_entries (consolidated) if available, else build from latest + past
      const normalizeEntries = (entries) => entries.map((e, idx) => ({
        ...e,
        // Map EmployeePortal field names → CheckDetailsEntry field names
        employer_name: e.employer_name || e.company_name || '',
        employee_code: e.employee_code || e.employee_id || '',
        location: e.location || e.location_address || '',
        date_of_joining: e.date_of_joining || e.employed_from || '',
        date_of_relieving: e.date_of_relieving || e.employed_to || '',
        salary: e.salary || e.salary_per_month || '',
        rm_contact: e.rm_contact || e.rm_contact_number || '',
        employment_type: e.employment_type || (idx === 0 ? 'current' : 'past'),
        can_verify_current: e.can_verify_current !== undefined ? e.can_verify_current : true
      }));
      if (employee.employment_entries && employee.employment_entries.length > 0) {
        setEmploymentEntries(normalizeEntries(employee.employment_entries));
      } else if (employee.latest_employment) {
        const pastEntries = employee.past_employments || employee.employment_history || [];
        setEmploymentEntries(normalizeEntries([employee.latest_employment, ...pastEntries]));
      } else {
        setEmploymentEntries(normalizeEntries(employee.past_employments || employee.employment_history || []));
      }
      
      // Education: try multiple data sources
      const normalizeEduEntries = (entries) => entries.map(e => ({
        ...e,
        // Map EmployeePortal field names → CheckDetailsEntry field names
        university: e.university || e.university_name || '',
        institution_name: e.institution_name || e.college || '',
        course: e.course || '',
        specialization: e.specialization || '',
        year_of_passing: e.year_of_passing || '',
        roll_no: e.roll_no || '',
        class_degree: e.class_degree || ''
      }));
      const eduEntries = employee.education_entries || employee.education_details || [];
      if (employee.highest_education) {
        setEducationEntries(normalizeEduEntries([employee.highest_education, ...(employee.prior_educations || [])]));
      } else {
        setEducationEntries(normalizeEduEntries(eduEntries));
      }
      
      // Address: handle both flat fields and nested object
      const pa = employee.present_address || {};
      const pma = employee.permanent_address || {};
      setAddressData({
        present: {
          house_no: employee.present_house_no || pa.address_line1 || pa.house_no || pa.complete_address || '',
          cross: employee.present_cross || pa.cross || '',
          main: employee.present_main || pa.main || '',
          area: employee.present_area || pa.address_line2 || pa.area || '',
          city: employee.present_city || pa.city || '',
          state: employee.present_state || pa.state || '',
          pincode: employee.present_pincode || pa.pincode || pa.zip_code || '',
          landmark: employee.present_landmark || pa.landmark || '',
          residence_type: employee.present_residence_type || pa.residence_type || '',
          period_of_stay: employee.present_period_of_stay || pa.period_of_stay || ''
        },
        permanent: {
          house_no: employee.permanent_house_no || pma.address_line1 || pma.house_no || pma.complete_address || '',
          cross: employee.permanent_cross || pma.cross || '',
          main: employee.permanent_main || pma.main || '',
          area: employee.permanent_area || pma.address_line2 || pma.area || '',
          city: employee.permanent_city || pma.city || '',
          state: employee.permanent_state || pma.state || '',
          pincode: employee.permanent_pincode || pma.pincode || pma.zip_code || '',
          landmark: employee.permanent_landmark || pma.landmark || '',
          residence_type: employee.permanent_residence_type || pma.residence_type || '',
          period_of_stay: employee.permanent_period_of_stay || pma.period_of_stay || ''
        },
        same_as_present: employee.permanent_same_as_present || false
      });
      
      // Load additional addresses
      if (employee.additional_addresses && employee.additional_addresses.length > 0) {
        setAdditionalAddresses(employee.additional_addresses.map(addr => ({
          address_type: addr.address_type || 'previous',
          house_no: addr.house_no || addr.complete_address?.split(',')[0] || '',
          cross: addr.cross || '',
          main: addr.main || '',
          area: addr.area || addr.complete_address || '',
          city: addr.city || '',
          state: addr.state || '',
          pincode: addr.pincode || '',
          landmark: addr.landmark || '',
          residence_type: addr.residence_type || '',
          period_of_stay: addr.period_of_stay || '',
          duration_from: addr.duration_from || '',
          duration_to: addr.duration_to || ''
        })));
      } else {
        setAdditionalAddresses([]);
      }
      
      setIdData({
        pan_number: employee.pan_number || employee.pan || '',
        aadhar_number: employee.aadhar_number || employee.aadhaar_number || '',
        dl_number: employee.dl_number || employee.driving_license || '',
        passport_number: employee.passport_number || ''
      });
      
      setReferencesData({
        personal: employee.personal_references || (employee.references || []).filter(r => r.type === 'personal') || [],
        professional: employee.professional_references || (employee.references || []).filter(r => r.type === 'professional') || []
      });
      
      // Load existing documents
      setDocuments(employee.documents || []);
      
    } catch (error) {
      // Only log errors, don't show toast for race conditions/brief errors
      console.error('Failed to load case details:', error);
    }
  };

  // Document upload handler
  const handleDocumentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !selectedCase || !employeeData) return;
    
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', 'other');
        formData.append('description', file.name);
        
        const response = await axios.post(`/employees/${employeeData.id}/documents`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        if (response.data) {
          setDocuments(prev => [...prev, response.data]);
          toast.success(`Uploaded: ${file.name}`);
        }
      } catch (error) {
        toast.error(`Failed to upload: ${file.name}`);
        console.error(error);
      }
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // Update document type
  const updateDocumentType = async (docId, newType) => {
    try {
      await axios.put(`/employees/${employeeData.id}/documents/${docId}`, {
        document_type: newType
      });
      setDocuments(prev => prev.map(d => 
        d.id === docId ? { ...d, document_type: newType } : d
      ));
      toast.success('Document type updated');
    } catch (error) {
      toast.error('Failed to update document type');
    }
  };

  // Delete document
  const deleteDocument = async (docId) => {
    if (!confirm('Are you sure you want to delete this document?')) return;
    
    try {
      await axios.delete(`/employees/${employeeData.id}/documents/${docId}`);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document deleted');
    } catch (error) {
      toast.error('Failed to delete document');
    }
  };

  // Handle Address Proof Upload
  const handleAddressProofUpload = async (file) => {
    if (!file || !selectedCase || !employeeData) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', 'address_proof');
      formData.append('description', `Address Proof - ${addressData.proof_type || 'Document'}`);
      
      const response = await axios.post(`/employees/${employeeData.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (response.data) {
        setAddressData(prev => ({
          ...prev,
          proof_document: { name: file.name, id: response.data.id },
          proof_document_url: response.data.url
        }));
        setDocuments(prev => [...prev, response.data]);
        toast.success(`Address proof uploaded: ${file.name}`);
      }
    } catch (error) {
      toast.error(`Failed to upload address proof`);
      console.error(error);
    }
    setUploading(false);
  };

  const handleSaveDetails = async () => {
    if (!selectedCase || !employeeData) return;
    
    setSaving(true);
    try {
      // Prepare data for update
      const updateData = {
        // Basic Info (editable by data entry — Feb 2026: name, email,
        // phone, dob, father_name unlocked so QC can fix wrong details
        // sent by client HR during initiation, BEFORE the candidate
        // submits the form).
        name: employeeData.name,
        email: employeeData.email,
        phone: employeeData.phone,
        dob: employeeData.dob,
        father_name: employeeData.father_name,
        gender: employeeData.gender,
        alternate_contact: employeeData.alternate_contact,
        
        // ID Details
        pan_number: idData.pan_number,
        aadhar_number: idData.aadhar_number,
        dl_number: idData.dl_number,
        passport_number: idData.passport_number,
        
        // Present Address
        present_house_no: addressData.present.house_no,
        present_cross: addressData.present.cross,
        present_main: addressData.present.main,
        present_area: addressData.present.area,
        present_city: addressData.present.city,
        present_state: addressData.present.state,
        present_pincode: addressData.present.pincode,
        present_landmark: addressData.present.landmark,
        present_residence_type: addressData.present.residence_type,
        present_period_of_stay: addressData.present.period_of_stay,
        
        // Permanent Address
        permanent_same_as_present: addressData.same_as_present,
        permanent_house_no: addressData.same_as_present ? addressData.present.house_no : addressData.permanent.house_no,
        permanent_cross: addressData.same_as_present ? addressData.present.cross : addressData.permanent.cross,
        permanent_main: addressData.same_as_present ? addressData.present.main : addressData.permanent.main,
        permanent_area: addressData.same_as_present ? addressData.present.area : addressData.permanent.area,
        permanent_city: addressData.same_as_present ? addressData.present.city : addressData.permanent.city,
        permanent_state: addressData.same_as_present ? addressData.present.state : addressData.permanent.state,
        permanent_pincode: addressData.same_as_present ? addressData.present.pincode : addressData.permanent.pincode,
        permanent_landmark: addressData.same_as_present ? addressData.present.landmark : addressData.permanent.landmark,
        permanent_residence_type: addressData.same_as_present ? addressData.present.residence_type : addressData.permanent.residence_type,
        permanent_period_of_stay: addressData.same_as_present ? addressData.present.period_of_stay : addressData.permanent.period_of_stay,
        
        // Additional Addresses
        additional_addresses: additionalAddresses.filter(a => a.area.trim() !== '' || a.house_no.trim() !== ''),
        
        // Employment
        is_fresher: employmentEntries.length === 0,
        latest_employment: employmentEntries.length > 0 ? employmentEntries[0] : null,
        past_employments: employmentEntries.slice(1),
        
        // Education
        highest_education: educationEntries.length > 0 ? educationEntries[0] : null,
        prior_educations: educationEntries.slice(1),
        
        // References
        personal_references: referencesData.personal,
        professional_references: referencesData.professional,
        
        // Fewer-records declarations (per-check-type) — declared on behalf of candidate
        employment_declaration: employmentDeclaration,
        education_declaration: educationDeclaration,
        address_declaration: addressDeclaration
      };
      
      await axios.put(`/employees/${employeeData.id}/details`, updateData);
      toast.success('Check details saved successfully');
      
    } catch (error) {
      toast.error('Failed to save details');
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  // Submit case on behalf of candidate - moves to Pending QC
  const handleSubmitCase = async () => {
    if (!selectedCase || !employeeData) return;
    
    setSubmitting(true);
    try {
      // First save all details
      await handleSaveDetails();
      
      // Then submit
      await axios.post(`/candidate-drafts/${employeeData.id}/submit`);
      toast.success('Case submitted to Pending QC successfully');
      setShowSubmitDialog(false);
      setSelectedCase(null);
      setEmployeeData(null);
      fetchCases();
    } catch (error) {
      toast.error('Failed to submit case: ' + (error.response?.data?.detail || error.message));
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  // Employment Entry Handlers
  const addEmploymentEntry = () => {
    setEmploymentEntries([...employmentEntries, {
      employer_name: '', employee_code: '', designation: '', location: '',
      date_of_joining: '', date_of_relieving: '', salary: '', reimbursements: '',
      uan_pf_number: '', employment_type: 'past',
      rm_name: '', rm_contact: '', rm_email: '', rm_designation: '', rm_department: '', rm_company: '',
      hr_name: '', hr_email: '', hr_phone: '',
      relieving_letter_file: '', relieving_letter_reason: '', payslip_file: '', payslip_reason: '',
      gap_reason: '', gap_reason_other: '', can_verify_current: true
    }]);
  };

  const updateEmploymentEntry = (index, field, value) => {
    const updated = [...employmentEntries];
    updated[index] = { ...updated[index], [field]: value };
    setEmploymentEntries(updated);
  };

  // Document upload handler for employment entries
  const handleDocUpload = async (entryIndex, docType, file) => {
    if (!file || !employeeData) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('description', `Employment ${entryIndex + 1} - ${docType}`);
      
      await axios.post(`/employees/${employeeData.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update the entry with file name
      const fieldName = docType === 'relieving_letter' ? 'relieving_letter_file' : 'payslip_file';
      updateEmploymentEntry(entryIndex, fieldName, file.name);
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      toast.error(`Failed to upload: ${file.name}`);
      console.error(error);
    }
  };

  // Education document upload handler
  const handleEduDocUpload = async (entryIndex, docType, file) => {
    if (!file || !employeeData) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('description', `Education ${entryIndex + 1} - ${docType}`);
      
      await axios.post(`/employees/${employeeData.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update the entry with file name
      const fieldName = docType === 'degree_certificate' ? 'certificate_file' : 'marksheet_file';
      updateEducationEntry(entryIndex, fieldName, file.name);
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      toast.error(`Failed to upload: ${file.name}`);
      console.error(error);
    }
  };

  // ID document upload handler
  const handleIdDocUpload = async (docType, file) => {
    if (!file || !employeeData) return;
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      formData.append('description', docType.replace(/_/g, ' ').toUpperCase());
      
      await axios.post(`/employees/${employeeData.id}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Update the ID data with file name
      setIdData(prev => ({ ...prev, [`${docType}_file`]: file.name }));
      toast.success(`Uploaded: ${file.name}`);
    } catch (error) {
      toast.error(`Failed to upload: ${file.name}`);
      console.error(error);
    }
  };

  const removeEmploymentEntry = (index) => {
    setEmploymentEntries(employmentEntries.filter((_, i) => i !== index));
  };

  // Education Entry Handlers
  const addEducationEntry = () => {
    setEducationEntries([...educationEntries, {
      education_type: '', university: '', institution_name: '', course: '',
      specialization: '', year_of_passing: '', roll_no: '', class_degree: '',
      name_as_per_doc: '', institution_address: ''
    }]);
  };

  const updateEducationEntry = (index, field, value) => {
    const updated = [...educationEntries];
    updated[index] = { ...updated[index], [field]: value };
    setEducationEntries(updated);
  };

  const removeEducationEntry = (index) => {
    setEducationEntries(educationEntries.filter((_, i) => i !== index));
  };

  // Reference Entry Handlers
  const addReference = (type) => {
    const newRef = { name: '', relationship: '', organization: '', designation: '', phone: '', email: '', known_since: '' };
    setReferencesData(prev => ({
      ...prev,
      [type]: [...prev[type], newRef]
    }));
  };

  const updateReference = (type, index, field, value) => {
    setReferencesData(prev => {
      const updated = [...prev[type]];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, [type]: updated };
    });
  };

  const removeReference = (type, index) => {
    setReferencesData(prev => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index)
    }));
  };

  // Filter cases by search
  const filteredCases = cases.filter(c => 
    !searchQuery || 
    c.id?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.employee_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.employee_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getCheckTypesForCase = (caseItem) => {
    const checks = caseItem.checks || [];
    return checks.map(c => {
      // Handle both string and object check_type
      if (typeof c.check_type === 'string') return c.check_type;
      if (typeof c === 'string') return c;
      if (c.check_type?.id) return c.check_type.id;
      if (c.check_type?.type) return c.check_type.type;
      if (c.check_type?.check_type) return c.check_type.check_type;
      if (c.type) return c.type;
      if (c.name) return c.name;
      return null;
    }).filter((v, i, a) => v && v !== '[object Object]' && a.indexOf(v) === i);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="check-details-entry">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <FileEdit className="w-8 h-8 text-purple-600" />
            Check Details Entry
          </h1>
          <p className="text-slate-600">Enter verification details for candidate checks</p>
        </div>
        <Button onClick={fetchCases} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      <div className="grid grid-cols-3 gap-6">
        {/* Case Selection Panel */}
        <Card className="col-span-1 border-slate-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg">Select Case</CardTitle>
            <div className="relative mt-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search case ID or employee..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent className="p-0 max-h-[600px] overflow-y-auto">
            {filteredCases.length === 0 ? (
              <div className="p-4 text-center text-slate-500">No cases found</div>
            ) : (
              filteredCases.map((caseItem) => (
                <div
                  key={caseItem.id}
                  onClick={() => loadCaseDetails(caseItem)}
                  className={`p-3 border-b cursor-pointer hover:bg-slate-50 transition-colors ${
                    selectedCase?.id === caseItem.id ? 'bg-purple-50 border-l-4 border-l-purple-600' : ''
                  }`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-mono text-sm font-medium">{caseItem.id}</p>
                      <p className="text-sm font-medium text-slate-900">{caseItem.employee_name}</p>
                      <p className="text-xs text-slate-500">{caseItem.employee_email}</p>
                    </div>
                    <Badge variant="outline" className="text-xs">
                      {(caseItem.checks || []).length} checks
                    </Badge>
                  </div>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {getCheckTypesForCase(caseItem).slice(0, 3).map((type, i) => (
                      <Badge key={i} className="text-xs bg-slate-100 text-slate-600">
                        {String(type || '').replace(/_/g, ' ')}
                      </Badge>
                    ))}
                    {getCheckTypesForCase(caseItem).length > 3 && (
                      <Badge className="text-xs bg-slate-100 text-slate-600">
                        +{getCheckTypesForCase(caseItem).length - 3}
                      </Badge>
                    )}
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Data Entry Panel */}
        <Card className="col-span-2 border-slate-200">
          {!selectedCase ? (
            <CardContent className="flex flex-col items-center justify-center h-[600px] text-slate-400">
              <FileEdit className="w-16 h-16 mb-4" />
              <p className="text-lg">Select a case to enter check details</p>
            </CardContent>
          ) : (
            <>
              <CardHeader className="border-b">
                <div className="flex justify-between items-start">
                  <div>
                    <CardTitle>{employeeData?.name}</CardTitle>
                    <p className="text-sm text-slate-500">{employeeData?.email} • {employeeData?.phone}</p>
                    <Badge variant="outline" className="mt-2">{selectedCase.id}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button onClick={handleSaveDetails} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
                      <Save className="w-4 h-4 mr-2" />
                      {saving ? 'Saving...' : 'Save All Details'}
                    </Button>
                    {(['initiated', 'basic_initiated'].includes(selectedCase?.status)) && (
                      <Button 
                        onClick={() => setShowSubmitDialog(true)} 
                        disabled={submitting}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid="submit-case-btn"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        Submit to QC
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="w-full justify-start rounded-none border-b bg-slate-50 p-0">
                    <TabsTrigger value="basic" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <User className="w-4 h-4 mr-2" />
                      Basic Info
                    </TabsTrigger>
                    <TabsTrigger value="employment" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <Briefcase className="w-4 h-4 mr-2" />
                      Employment
                    </TabsTrigger>
                    <TabsTrigger value="education" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <GraduationCap className="w-4 h-4 mr-2" />
                      Education
                    </TabsTrigger>
                    <TabsTrigger value="address" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      Address
                    </TabsTrigger>
                    <TabsTrigger value="id" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <CreditCard className="w-4 h-4 mr-2" />
                      ID Details
                    </TabsTrigger>
                    <TabsTrigger value="references" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <Users className="w-4 h-4 mr-2" />
                      References
                    </TabsTrigger>
                    <TabsTrigger value="review" className="rounded-none data-[state=active]:border-b-2 data-[state=active]:border-purple-600">
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Review & Submit
                    </TabsTrigger>
                  </TabsList>

                  {/* Basic Info Tab */}
                  <TabsContent value="basic" className="p-4 max-h-[450px] overflow-y-auto">
                    {/* Employee Basic Details */}
                    <Card className="border-slate-200 mb-4">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2">
                          <User className="w-5 h-5 text-purple-600" />
                          Employee Basic Information
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="space-y-3">
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <Label className="text-xs">Full Name</Label>
                            <Input
                              data-testid="basic-info-name-input"
                              value={employeeData?.name || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, name: e.target.value}))}
                              placeholder="Full name as per PAN / Aadhaar"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Email</Label>
                            <Input
                              data-testid="basic-info-email-input"
                              type="email"
                              value={employeeData?.email || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, email: e.target.value.trim()}))}
                              placeholder="candidate@example.com"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Phone</Label>
                            <Input
                              data-testid="basic-info-phone-input"
                              value={employeeData?.phone || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, phone: e.target.value}))}
                              placeholder="10-digit or +91xxxxxxxxxx"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Date of Birth</Label>
                            <Input
                              data-testid="basic-info-dob-input"
                              type="date"
                              value={employeeData?.dob || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, dob: e.target.value}))}
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Father&apos;s Name</Label>
                            <Input
                              data-testid="basic-info-father-name-input"
                              value={employeeData?.father_name || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, father_name: e.target.value}))}
                              placeholder="Father's name as per PAN"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Gender</Label>
                            <select 
                              className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                              value={employeeData?.gender || ''}
                              onChange={(e) => setEmployeeData(prev => ({...prev, gender: e.target.value}))}
                            >
                              <option value="">Select</option>
                              <option value="male">Male</option>
                              <option value="female">Female</option>
                              <option value="other">Other</option>
                            </select>
                          </div>
                          <div>
                            <Label className="text-xs">Alternate Contact Number</Label>
                            <Input 
                              value={employeeData?.alternate_contact || ''} 
                              onChange={(e) => setEmployeeData(prev => ({...prev, alternate_contact: e.target.value.replace(/\D/g, '').slice(0, 10)}))}
                              placeholder="10-digit number"
                              maxLength={10}
                            />
                          </div>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">
                          Basic details unlocked so Data Entry / QC can fix wrong details sent by client HR before the candidate submits. Any change is written to the employee record on Save.
                        </p>
                      </CardContent>
                    </Card>

                    {/* Auto-Filled Data Preview */}
                    <Card className="border-blue-200 bg-blue-50 shadow-sm">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-lg flex items-center gap-2 text-blue-900">
                          <FileText className="w-5 h-5" />
                          Auto-Filled Verification Data Preview
                        </CardTitle>
                        <p className="text-sm text-blue-700">The following data will be automatically copied for various verification checks</p>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {/* World Database Check */}
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                            World Database Check
                          </h4>
                          <p className="text-xs text-slate-500 mb-2">Copies: Basic Details (Name, DOB, Father Name)</p>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Name:</span>
                              <span className="ml-2 font-medium">{employeeData?.name || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">DOB:</span>
                              <span className="ml-2 font-medium">{employeeData?.dob || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Father&apos;s Name:</span>
                              <span className="ml-2 font-medium">{employeeData?.father_name || '-'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Credit Check */}
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                            Credit Check
                          </h4>
                          <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN Card</p>
                          <div className="grid grid-cols-3 gap-3 text-sm">
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Name:</span>
                              <span className="ml-2 font-medium">{employeeData?.name || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">DOB:</span>
                              <span className="ml-2 font-medium">{employeeData?.dob || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">PAN Number:</span>
                              <span className="ml-2 font-medium">{idData.pan_number || '-'}</span>
                            </div>
                          </div>
                        </div>

                        {/* ID Check */}
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                            ID Verification (PAN / Aadhar)
                          </h4>
                          <p className="text-xs text-slate-500 mb-2">Copies: Basic Details + PAN/Aadhar Number</p>
                          <div className="grid grid-cols-4 gap-3 text-sm">
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Name:</span>
                              <span className="ml-2 font-medium">{employeeData?.name || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">DOB:</span>
                              <span className="ml-2 font-medium">{employeeData?.dob || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">PAN:</span>
                              <span className="ml-2 font-medium">{idData.pan_number || '-'}</span>
                            </div>
                            <div className="bg-slate-50 p-2 rounded">
                              <span className="text-slate-500">Aadhar:</span>
                              <span className="ml-2 font-medium">{idData.aadhar_number || '-'}</span>
                            </div>
                          </div>
                        </div>

                        {/* Criminal Court & Advocate Check */}
                        <div className="bg-white rounded-lg p-4 border border-blue-100">
                          <h4 className="font-semibold text-slate-800 mb-2 flex items-center gap-2">
                            <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                            Criminal Court / Criminal Advocate Check
                          </h4>
                          <p className="text-xs text-slate-500 mb-2">Copies: Present Address + Subsequent Addresses</p>
                          <div className="space-y-2 text-sm">
                            {(addressData.present.house_no || addressData.present.area) && (
                              <div className="bg-slate-50 p-2 rounded">
                                <span className="text-slate-500">Present Address:</span>
                                <span className="ml-2 font-medium">
                                  {[addressData.present.house_no, addressData.present.cross, addressData.present.main, addressData.present.area, addressData.present.city, addressData.present.state, addressData.present.pincode].filter(Boolean).join(', ') || '-'}
                                </span>
                              </div>
                            )}
                            {(addressData.permanent.house_no || addressData.permanent.area) && !addressData.same_as_present && (
                              <div className="bg-slate-50 p-2 rounded">
                                <span className="text-slate-500">Permanent Address:</span>
                                <span className="ml-2 font-medium">
                                  {[addressData.permanent.house_no, addressData.permanent.cross, addressData.permanent.main, addressData.permanent.area, addressData.permanent.city, addressData.permanent.state, addressData.permanent.pincode].filter(Boolean).join(', ') || '-'}
                                </span>
                              </div>
                            )}
                            {additionalAddresses.filter(a => a.house_no || a.area).map((addr, idx) => (
                              <div key={idx} className="bg-slate-50 p-2 rounded">
                                <span className="text-slate-500">Additional Address {idx + 1} ({addr.address_type}):</span>
                                <span className="ml-2 font-medium">
                                  {[addr.house_no, addr.cross, addr.main, addr.area, addr.city, addr.state, addr.pincode].filter(Boolean).join(', ') || '-'}
                                </span>
                              </div>
                            ))}
                            {!addressData.present.house_no && !addressData.present.area && !addressData.permanent.house_no && !addressData.permanent.area && additionalAddresses.length === 0 && (
                              <div className="bg-amber-50 p-2 rounded text-amber-700">
                                No addresses filled yet. Please fill address details in the Address tab.
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* Employment Tab */}
                  <TabsContent value="employment" className="p-4 max-h-[450px] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">Employment History</h3>
                      <Button onClick={addEmploymentEntry} size="sm" variant="outline">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Employment
                      </Button>
                    </div>
                    
                    {employmentEntries.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <Briefcase className="w-12 h-12 mx-auto mb-2" />
                        <p>No employment entries. Click "Add Employment" to add.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {employmentEntries.map((entry, idx) => (
                          <Card key={idx} className="border-slate-200">
                            <CardHeader className="py-3 flex flex-row items-center justify-between">
                              <CardTitle className="text-sm">
                                Employment {idx + 1} {idx === 0 && <Badge className="ml-2 bg-purple-100 text-purple-700">Latest</Badge>}
                              </CardTitle>
                              <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => removeEmploymentEntry(idx)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="pt-0">
                              {/* Employment Type — positioned at top to mirror candidate portal */}
                              <div className="mb-3 pb-3 border-b border-slate-100">
                                <div className="grid grid-cols-3 gap-3">
                                  <div>
                                    <Label className="text-xs">Employment Type *</Label>
                                    <select
                                      className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white"
                                      value={entry.employment_type || (idx === 0 ? 'current' : 'past')}
                                      onChange={(e) => updateEmploymentEntry(idx, 'employment_type', e.target.value)}
                                      data-testid={`employment-type-${idx}`}
                                    >
                                      <option value="current">Current</option>
                                      <option value="past">Past</option>
                                    </select>
                                  </div>
                                </div>
                                {/* Verification from current employer toggle — only for current employment */}
                                {(entry.employment_type || (idx === 0 ? 'current' : 'past')) === 'current' && (
                                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md space-y-3">
                                    <div className="flex items-center gap-2">
                                      <input
                                        type="checkbox"
                                        id={`can-verify-${idx}`}
                                        checked={entry.can_verify_current !== false}
                                        onChange={(e) => updateEmploymentEntry(idx, 'can_verify_current', e.target.checked)}
                                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                                        data-testid={`can-verify-current-${idx}`}
                                      />
                                      <Label htmlFor={`can-verify-${idx}`} className="text-sm font-normal cursor-pointer">
                                        Verification from current employer is permitted
                                      </Label>
                                    </div>
                                    {entry.can_verify_current === false && (
                                      <div>
                                        <Label className="text-sm">Date of Relieving *</Label>
                                        <Input
                                          type="date"
                                          value={entry.date_of_relieving || ''}
                                          onChange={(e) => updateEmploymentEntry(idx, 'date_of_relieving', e.target.value)}
                                          className={!entry.date_of_relieving ? 'border-amber-400' : ''}
                                          data-testid={`date-of-relieving-verify-${idx}`}
                                        />
                                        {entry.date_of_relieving && <span className="text-xs text-slate-500">{formatDateForDisplay(entry.date_of_relieving)}</span>}
                                        {!entry.date_of_relieving && (
                                          <p className="text-xs text-amber-600 mt-1">Date of relieving is required when verification from current employer is not permitted</p>
                                        )}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">Employer Name *</Label>
                                  <Input value={entry.employer_name || ''} onChange={(e) => updateEmploymentEntry(idx, 'employer_name', e.target.value)} placeholder="Company name" />
                                </div>
                                <div>
                                  <Label className="text-xs">Employee Code</Label>
                                  <Input value={entry.employee_code || ''} onChange={(e) => updateEmploymentEntry(idx, 'employee_code', e.target.value)} placeholder="Employee ID" />
                                </div>
                                <div>
                                  <Label className="text-xs">Designation *</Label>
                                  <Input value={entry.designation || ''} onChange={(e) => updateEmploymentEntry(idx, 'designation', e.target.value)} placeholder="Job title" />
                                </div>
                                <div>
                                  <Label className="text-xs">Location</Label>
                                  <Input value={entry.location || ''} onChange={(e) => updateEmploymentEntry(idx, 'location', e.target.value)} placeholder="City" />
                                </div>
                                <div>
                                  <Label className="text-xs">Date of Joining</Label>
                                  <Input type="date" value={entry.date_of_joining || ''} onChange={(e) => updateEmploymentEntry(idx, 'date_of_joining', e.target.value)} />
                                  {entry.date_of_joining && <span className="text-xs text-slate-500">{formatDateForDisplay(entry.date_of_joining)}</span>}
                                </div>
                                <div>
                                  <Label className="text-xs">
                                    {(entry.employment_type || (idx === 0 ? 'current' : 'past')) === 'current' ? 'Employed To (Present)' : 'Date of Relieving'}
                                  </Label>
                                  <Input 
                                    type="date" 
                                    value={entry.date_of_relieving || ''} 
                                    onChange={(e) => updateEmploymentEntry(idx, 'date_of_relieving', e.target.value)} 
                                    disabled={(entry.employment_type || (idx === 0 ? 'current' : 'past')) === 'current' && entry.can_verify_current === false}
                                  />
                                  {entry.date_of_relieving && <span className="text-xs text-slate-500">{formatDateForDisplay(entry.date_of_relieving)}</span>}
                                </div>
                                <div>
                                  <Label className="text-xs">Salary (CTC)</Label>
                                  <Input value={entry.salary || ''} onChange={(e) => updateEmploymentEntry(idx, 'salary', e.target.value)} placeholder="₹ per annum" />
                                </div>
                                <div>
                                  <Label className="text-xs">Reimbursements</Label>
                                  <Input value={entry.reimbursements || ''} onChange={(e) => updateEmploymentEntry(idx, 'reimbursements', e.target.value)} placeholder="Variable pay, bonus, etc." />
                                </div>
                                <div>
                                  <Label className="text-xs">UAN / PF Number</Label>
                                  <Input value={entry.uan_pf_number || ''} onChange={(e) => updateEmploymentEntry(idx, 'uan_pf_number', e.target.value)} placeholder="e.g., 100123456789" />
                                </div>
                                
                                <div className="col-span-3 border-t pt-3 mt-2">
                                  <p className="text-xs font-medium text-slate-500 mb-2">HR Contact Details</p>
                                  <div className="grid grid-cols-3 gap-3">
                                    <div>
                                      <Label className="text-xs">HR Name</Label>
                                      <Input value={entry.hr_name || ''} onChange={(e) => updateEmploymentEntry(idx, 'hr_name', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">HR Email</Label>
                                      <Input type="email" value={entry.hr_email || ''} onChange={(e) => updateEmploymentEntry(idx, 'hr_email', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">HR Phone</Label>
                                      <Input 
                                        value={entry.hr_phone || ''} 
                                        onChange={(e) => updateEmploymentEntry(idx, 'hr_phone', formatPhoneMax10(e.target.value))} 
                                        maxLength={10}
                                        className={entry.hr_phone && entry.hr_phone.length !== 10 ? 'border-red-400' : ''}
                                      />
                                    </div>
                                  </div>
                                </div>
                                
                                <div className="col-span-3 border-t pt-3 mt-2">
                                  <p className="text-xs font-medium text-slate-500 mb-2">Reporting Manager Details</p>
                                  <div className="grid grid-cols-3 gap-3">
                                    <div>
                                      <Label className="text-xs">RM Name *</Label>
                                      <Input value={entry.rm_name || ''} onChange={(e) => updateEmploymentEntry(idx, 'rm_name', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">RM Phone *</Label>
                                      <Input 
                                        value={entry.rm_contact || ''} 
                                        onChange={(e) => updateEmploymentEntry(idx, 'rm_contact', formatPhoneMax10(e.target.value))} 
                                        maxLength={10}
                                        className={entry.rm_contact && entry.rm_contact.length !== 10 ? 'border-red-400' : ''}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">RM Email</Label>
                                      <Input type="email" value={entry.rm_email || ''} onChange={(e) => updateEmploymentEntry(idx, 'rm_email', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">RM Designation</Label>
                                      <Input value={entry.rm_designation || ''} onChange={(e) => updateEmploymentEntry(idx, 'rm_designation', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">RM Department</Label>
                                      <Input value={entry.rm_department || ''} onChange={(e) => updateEmploymentEntry(idx, 'rm_department', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">RM Company (if different)</Label>
                                      <Input value={entry.rm_company || ''} onChange={(e) => updateEmploymentEntry(idx, 'rm_company', e.target.value)} />
                                    </div>
                                  </div>
                                </div>

                                {/* Employment Gap Detection */}
                                <div className="col-span-3 border-t pt-3 mt-2">
                                  {idx < employmentEntries.length - 1 && (() => {
                                    const nextEntry = employmentEntries[idx + 1];
                                    const currentJoining = entry.date_of_joining;
                                    const nextRelieving = nextEntry?.date_of_relieving;
                                    if (currentJoining && nextRelieving) {
                                      const joinDate = new Date(currentJoining);
                                      const relieveDate = new Date(nextRelieving);
                                      const gapDays = Math.floor((joinDate - relieveDate) / (1000 * 60 * 60 * 24));
                                      if (gapDays > 30) {
                                        const gapMonths = Math.floor(gapDays / 30);
                                        return (
                                          <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3">
                                            <div className="flex items-center gap-2">
                                              <AlertTriangle className="w-5 h-5 text-amber-600" />
                                              <div>
                                                <p className="font-medium text-amber-800">Employment Gap Detected</p>
                                                <p className="text-sm text-amber-700">
                                                  {gapDays} days gap between {nextEntry.employer_name || 'Previous Employment'} ({nextRelieving}) and this employment ({currentJoining})
                                                </p>
                                              </div>
                                            </div>
                                            <div className="space-y-2">
                                              <Label className="text-sm font-medium text-amber-800">Reason for Gap *</Label>
                                              <select
                                                value={entry.gap_reason || ''}
                                                onChange={(e) => updateEmploymentEntry(idx, 'gap_reason', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-md text-sm ${!entry.gap_reason ? 'border-amber-400' : 'border-slate-200'}`}
                                                data-testid={`gap-reason-${idx}`}
                                              >
                                                <option value="">-- Select Reason --</option>
                                                {GAP_REASONS.map(reason => (
                                                  <option key={reason} value={reason}>{reason}</option>
                                                ))}
                                              </select>
                                              {entry.gap_reason === 'Other' && (
                                                <div>
                                                  <Input
                                                    value={entry.gap_reason_other || ''}
                                                    onChange={(e) => updateEmploymentEntry(idx, 'gap_reason_other', e.target.value)}
                                                    placeholder="Please specify the reason"
                                                    className={!entry.gap_reason_other ? 'border-amber-400' : ''}
                                                    data-testid={`gap-reason-other-${idx}`}
                                                  />
                                                  {!entry.gap_reason_other && (
                                                    <p className="text-xs text-amber-600 mt-1">Please specify the reason for the employment gap</p>
                                                  )}
                                                </div>
                                              )}
                                              {!entry.gap_reason && (
                                                <p className="text-xs text-amber-600">Please select a reason for the employment gap</p>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      }
                                    }
                                    return null;
                                  })()}
                                </div>

                                {/* Document Uploads */}
                                <div className="col-span-3 border-t pt-3 mt-2">
                                  <p className="text-xs font-medium text-slate-500 mb-2">Document Uploads *</p>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-xs mb-1 block">Upload Relieving Letter (PDF/JPG) *</Label>
                                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-purple-300 cursor-pointer transition-colors">
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => handleDocUpload(idx, 'relieving_letter', e.target.files[0])}
                                          className="hidden"
                                          id={`relieving-${idx}`}
                                        />
                                        <label htmlFor={`relieving-${idx}`} className="cursor-pointer flex items-center justify-center gap-2 text-slate-500 text-sm">
                                          <Upload className="w-4 h-4" />
                                          {entry.relieving_letter_file ? entry.relieving_letter_file : 'Choose File'}
                                        </label>
                                      </div>
                                      <Input 
                                        className="mt-2 text-xs" 
                                        placeholder="Reason if not available" 
                                        value={entry.relieving_letter_reason || ''} 
                                        onChange={(e) => updateEmploymentEntry(idx, 'relieving_letter_reason', e.target.value)} 
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs mb-1 block">Upload Latest Payslip (PDF/JPG) *</Label>
                                      <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-purple-300 cursor-pointer transition-colors">
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => handleDocUpload(idx, 'payslip', e.target.files[0])}
                                          className="hidden"
                                          id={`payslip-${idx}`}
                                        />
                                        <label htmlFor={`payslip-${idx}`} className="cursor-pointer flex items-center justify-center gap-2 text-slate-500 text-sm">
                                          <Upload className="w-4 h-4" />
                                          {entry.payslip_file ? entry.payslip_file : 'Choose File'}
                                        </label>
                                      </div>
                                      <Input 
                                        className="mt-2 text-xs" 
                                        placeholder="Reason if not available" 
                                        value={entry.payslip_reason || ''} 
                                        onChange={(e) => updateEmploymentEntry(idx, 'payslip_reason', e.target.value)} 
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Education Tab */}
                  <TabsContent value="education" className="p-4 max-h-[450px] overflow-y-auto">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-medium">Education History</h3>
                      <Button onClick={addEducationEntry} size="sm" variant="outline">
                        <Plus className="w-4 h-4 mr-1" />
                        Add Education
                      </Button>
                    </div>
                    
                    {educationEntries.length === 0 ? (
                      <div className="text-center py-8 text-slate-400">
                        <GraduationCap className="w-12 h-12 mx-auto mb-2" />
                        <p>No education entries. Click "Add Education" to add.</p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {educationEntries.map((entry, idx) => (
                          <Card key={idx} className="border-slate-200">
                            <CardHeader className="py-3 flex flex-row items-center justify-between">
                              <CardTitle className="text-sm">
                                Education {idx + 1} {idx === 0 && <Badge className="ml-2 bg-emerald-100 text-emerald-700">Highest</Badge>}
                              </CardTitle>
                              <Button variant="ghost" size="sm" className="text-rose-600" onClick={() => removeEducationEntry(idx)}>
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="pt-0">
                              <div className="grid grid-cols-3 gap-3">
                                <div>
                                  <Label className="text-xs">Education Type *</Label>
                                  <Select value={entry.education_type || ''} onValueChange={(v) => updateEducationEntry(idx, 'education_type', v)}>
                                    <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                    <SelectContent>
                                      {EDUCATION_TYPES.map(type => (
                                        <SelectItem key={type} value={type}>{type}</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">University *</Label>
                                  <Input value={entry.university || ''} onChange={(e) => updateEducationEntry(idx, 'university', e.target.value)} placeholder="University name" />
                                </div>
                                <div>
                                  <Label className="text-xs">Institution/College *</Label>
                                  <Input value={entry.institution_name || ''} onChange={(e) => updateEducationEntry(idx, 'institution_name', e.target.value)} placeholder="College name" />
                                </div>
                                <div>
                                  <Label className="text-xs">Course/Degree</Label>
                                  <Input value={entry.course || ''} onChange={(e) => updateEducationEntry(idx, 'course', e.target.value)} placeholder="B.Tech, MBA, etc." />
                                </div>
                                <div>
                                  <Label className="text-xs">Specialization</Label>
                                  <Input value={entry.specialization || ''} onChange={(e) => updateEducationEntry(idx, 'specialization', e.target.value)} placeholder="Computer Science" />
                                </div>
                                <div>
                                  <Label className="text-xs">Year of Passing</Label>
                                  <Input value={entry.year_of_passing || ''} onChange={(e) => updateEducationEntry(idx, 'year_of_passing', e.target.value)} placeholder="2020" />
                                </div>
                                <div>
                                  <Label className="text-xs">Roll No / Reg No</Label>
                                  <Input value={entry.roll_no || ''} onChange={(e) => updateEducationEntry(idx, 'roll_no', e.target.value)} />
                                </div>
                                <div>
                                  <Label className="text-xs">Class / CGPA</Label>
                                  <Input value={entry.class_degree || ''} onChange={(e) => updateEducationEntry(idx, 'class_degree', e.target.value)} placeholder="First Class / 8.5 CGPA" />
                                </div>
                                <div>
                                  <Label className="text-xs">Name as per Document</Label>
                                  <Input value={entry.name_as_per_doc || ''} onChange={(e) => updateEducationEntry(idx, 'name_as_per_doc', e.target.value)} />
                                </div>
                                <div className="col-span-3">
                                  <Label className="text-xs">Institution Address</Label>
                                  <Textarea value={entry.institution_address || ''} onChange={(e) => updateEducationEntry(idx, 'institution_address', e.target.value)} rows={2} />
                                </div>
                                
                                {/* Document Uploads */}
                                <div className="col-span-3 border-t pt-3 mt-2">
                                  <p className="text-xs font-medium text-slate-500 mb-2">Document Uploads *</p>
                                  <div className="grid grid-cols-2 gap-4">
                                    <div>
                                      <Label className="text-xs mb-1 block">Upload Degree/Certificate (PDF/JPG) *</Label>
                                      <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${entry.certificate_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => handleEduDocUpload(idx, 'degree_certificate', e.target.files[0])}
                                          className="hidden"
                                          id={`certificate-${idx}`}
                                        />
                                        <label htmlFor={`certificate-${idx}`} className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${entry.certificate_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                          {entry.certificate_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                          {entry.certificate_file ? entry.certificate_file : 'Choose File'}
                                        </label>
                                      </div>
                                      <Input 
                                        className="mt-2 text-xs" 
                                        placeholder="Reason if not available" 
                                        value={entry.certificate_reason || ''} 
                                        onChange={(e) => updateEducationEntry(idx, 'certificate_reason', e.target.value)} 
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs mb-1 block">Upload Marksheet (PDF/JPG) *</Label>
                                      <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${entry.marksheet_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                        <input
                                          type="file"
                                          accept=".pdf,.jpg,.jpeg,.png"
                                          onChange={(e) => handleEduDocUpload(idx, 'marksheet', e.target.files[0])}
                                          className="hidden"
                                          id={`marksheet-${idx}`}
                                        />
                                        <label htmlFor={`marksheet-${idx}`} className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${entry.marksheet_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                          {entry.marksheet_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                          {entry.marksheet_file ? entry.marksheet_file : 'Choose File'}
                                        </label>
                                      </div>
                                      <Input 
                                        className="mt-2 text-xs" 
                                        placeholder="Reason if not available" 
                                        value={entry.marksheet_reason || ''} 
                                        onChange={(e) => updateEducationEntry(idx, 'marksheet_reason', e.target.value)} 
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </TabsContent>

                  {/* Address Tab */}
                  <TabsContent value="address" className="p-4 max-h-[450px] overflow-y-auto">
                    <div className="space-y-4">
                      <Card className="border-slate-200">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            Present Address
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <Label className="text-xs">House No / Flat No</Label>
                              <Input value={addressData.present.house_no} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, house_no: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">Cross</Label>
                              <Input value={addressData.present.cross} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, cross: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">Main</Label>
                              <Input value={addressData.present.main} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, main: e.target.value } }))} />
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Area / Locality</Label>
                              <Input value={addressData.present.area} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, area: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">Landmark</Label>
                              <Input value={addressData.present.landmark} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, landmark: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">City</Label>
                              <Input value={addressData.present.city} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, city: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">State</Label>
                              <Select value={addressData.present.state} onValueChange={(v) => setAddressData(prev => ({ ...prev, present: { ...prev.present, state: v } }))}>
                                <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                                <SelectContent>
                                  {INDIAN_STATES.map(state => (
                                    <SelectItem key={state} value={state}>{state}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Pincode</Label>
                              <Input value={addressData.present.pincode} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, pincode: e.target.value } }))} />
                            </div>
                            <div>
                              <Label className="text-xs">Residence Type</Label>
                              <Select value={addressData.present.residence_type} onValueChange={(v) => setAddressData(prev => ({ ...prev, present: { ...prev.present, residence_type: v } }))}>
                                <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                <SelectContent>
                                  {RESIDENCE_TYPES.map(type => (
                                    <SelectItem key={type} value={type}>{type}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs">Period of Stay</Label>
                              <Input value={addressData.present.period_of_stay} onChange={(e) => setAddressData(prev => ({ ...prev, present: { ...prev.present, period_of_stay: e.target.value } }))} placeholder="e.g., 2 years" />
                            </div>
                          </div>
                        </CardContent>
                      </Card>

                      <div className="flex items-center gap-2 px-2">
                        <Switch
                          checked={addressData.same_as_present}
                          onCheckedChange={(checked) => setAddressData(prev => ({ ...prev, same_as_present: checked }))}
                        />
                        <Label className="text-sm">Permanent address same as present</Label>
                      </div>

                      {!addressData.same_as_present && (
                        <Card className="border-slate-200">
                          <CardHeader className="py-3">
                            <CardTitle className="text-sm flex items-center gap-2">
                              <MapPin className="w-4 h-4" />
                              Permanent Address
                            </CardTitle>
                          </CardHeader>
                          <CardContent className="pt-0">
                            <div className="grid grid-cols-3 gap-3">
                              <div>
                                <Label className="text-xs">House No / Flat No</Label>
                                <Input value={addressData.permanent.house_no} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, house_no: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">Cross</Label>
                                <Input value={addressData.permanent.cross} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, cross: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">Main</Label>
                                <Input value={addressData.permanent.main} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, main: e.target.value } }))} />
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs">Area / Locality</Label>
                                <Input value={addressData.permanent.area} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, area: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">Landmark</Label>
                                <Input value={addressData.permanent.landmark} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, landmark: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">City</Label>
                                <Input value={addressData.permanent.city} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, city: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">State</Label>
                                <Select value={addressData.permanent.state} onValueChange={(v) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, state: v } }))}>
                                  <SelectTrigger><SelectValue placeholder="Select state" /></SelectTrigger>
                                  <SelectContent>
                                    {INDIAN_STATES.map(state => (
                                      <SelectItem key={state} value={state}>{state}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Pincode</Label>
                                <Input value={addressData.permanent.pincode} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, pincode: e.target.value } }))} />
                              </div>
                              <div>
                                <Label className="text-xs">Residence Type</Label>
                                <Select value={addressData.permanent.residence_type} onValueChange={(v) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, residence_type: v } }))}>
                                  <SelectTrigger><SelectValue placeholder="Select type" /></SelectTrigger>
                                  <SelectContent>
                                    {RESIDENCE_TYPES.map(type => (
                                      <SelectItem key={type} value={type}>{type}</SelectItem>
                                    ))}
                                  </SelectContent>
                                </Select>
                              </div>
                              <div>
                                <Label className="text-xs">Period of Stay</Label>
                                <Input value={addressData.permanent.period_of_stay} onChange={(e) => setAddressData(prev => ({ ...prev, permanent: { ...prev.permanent, period_of_stay: e.target.value } }))} placeholder="e.g., 5 years" />
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Address Proof Documents */}
                      <Card className="border-slate-200 mt-4">
                        <CardHeader className="py-3">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Address Proof Documents
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Address Proof Type</Label>
                              <Select 
                                value={addressData.proof_type || ''} 
                                onValueChange={(v) => setAddressData(prev => ({ ...prev, proof_type: v }))}
                              >
                                <SelectTrigger><SelectValue placeholder="Select proof type" /></SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="utility_bill">Utility Bill (Electricity/Water/Gas)</SelectItem>
                                  <SelectItem value="rental_agreement">Rental Agreement</SelectItem>
                                  <SelectItem value="bank_statement">Bank Statement</SelectItem>
                                  <SelectItem value="voter_id">Voter ID</SelectItem>
                                  <SelectItem value="passport">Passport</SelectItem>
                                  <SelectItem value="ration_card">Ration Card</SelectItem>
                                  <SelectItem value="property_tax">Property Tax Receipt</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Upload Address Proof (PDF/JPG) *</Label>
                              <div className="border-2 border-dashed border-slate-200 rounded-lg p-3 text-center hover:border-purple-300 cursor-pointer transition-colors">
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleAddressProofUpload(e.target.files[0])}
                                  className="hidden"
                                  id="address-proof-upload"
                                />
                                <label htmlFor="address-proof-upload" className="cursor-pointer flex items-center justify-center gap-2 text-slate-500 text-sm">
                                  <Upload className="w-4 h-4" />
                                  {addressData.proof_document ? (
                                    <span className="text-purple-600 font-medium">{addressData.proof_document.name || 'Document uploaded'}</span>
                                  ) : (
                                    'Click to upload'
                                  )}
                                </label>
                              </div>
                              {addressData.proof_document_url && (
                                <a 
                                  href={addressData.proof_document_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs text-purple-600 hover:underline mt-1 block"
                                >
                                  View uploaded document
                                </a>
                              )}
                            </div>
                            <div className="col-span-2">
                              <Label className="text-xs">Document Reference Number (if any)</Label>
                              <Input 
                                value={addressData.proof_reference || ''} 
                                onChange={(e) => setAddressData(prev => ({ ...prev, proof_reference: e.target.value }))}
                                placeholder="e.g., Bill number, Agreement number"
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Additional Addresses */}
                    {additionalAddresses.length > 0 && (
                      <div className="space-y-3 mt-4">
                        <h4 className="font-medium text-slate-700">Additional Addresses</h4>
                        {additionalAddresses.map((addr, index) => (
                          <Card key={index} className="border-slate-200">
                            <CardHeader className="py-2 flex flex-row items-center justify-between">
                              <div className="flex items-center gap-2">
                                <CardTitle className="text-sm">Additional Address {index + 1}</CardTitle>
                                <Select value={addr.address_type} onValueChange={(v) => updateAdditionalAddress(index, 'address_type', v)}>
                                  <SelectTrigger className="w-[140px] h-7 text-xs">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="previous">Previous</SelectItem>
                                    <SelectItem value="native">Native Place</SelectItem>
                                    <SelectItem value="office">Office</SelectItem>
                                    <SelectItem value="other">Other</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                              <Button variant="ghost" size="sm" onClick={() => removeAdditionalAddress(index)} className="text-red-600 hover:text-red-700 h-7">
                                <X className="w-4 h-4" />
                              </Button>
                            </CardHeader>
                            <CardContent className="py-2">
                              <div className="grid grid-cols-6 gap-2">
                                <div>
                                  <Label className="text-xs">House No</Label>
                                  <Input value={addr.house_no} onChange={(e) => updateAdditionalAddress(index, 'house_no', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">Cross</Label>
                                  <Input value={addr.cross} onChange={(e) => updateAdditionalAddress(index, 'cross', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">Main</Label>
                                  <Input value={addr.main} onChange={(e) => updateAdditionalAddress(index, 'main', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">Area</Label>
                                  <Input value={addr.area} onChange={(e) => updateAdditionalAddress(index, 'area', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">Landmark</Label>
                                  <Input value={addr.landmark} onChange={(e) => updateAdditionalAddress(index, 'landmark', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">Pincode</Label>
                                  <Input value={addr.pincode} onChange={(e) => updateAdditionalAddress(index, 'pincode', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">City</Label>
                                  <Input value={addr.city} onChange={(e) => updateAdditionalAddress(index, 'city', e.target.value)} className="h-8 text-sm" />
                                </div>
                                <div>
                                  <Label className="text-xs">State</Label>
                                  <Select value={addr.state} onValueChange={(v) => updateAdditionalAddress(index, 'state', v)}>
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {INDIAN_STATES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Residence Type</Label>
                                  <Select value={addr.residence_type} onValueChange={(v) => updateAdditionalAddress(index, 'residence_type', v)}>
                                    <SelectTrigger className="h-8 text-xs">
                                      <SelectValue placeholder="Select" />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {RESIDENCE_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                                    </SelectContent>
                                  </Select>
                                </div>
                                <div>
                                  <Label className="text-xs">Stayed From</Label>
                                  <Input type="date" value={addr.duration_from} onChange={(e) => updateAdditionalAddress(index, 'duration_from', e.target.value)} className="h-8 text-sm" />
                                  {addr.duration_from && <span className="text-xs text-slate-500">{formatDateForDisplay(addr.duration_from)}</span>}
                                </div>
                                <div>
                                  <Label className="text-xs">Stayed Till</Label>
                                  <Input type="date" value={addr.duration_to} onChange={(e) => updateAdditionalAddress(index, 'duration_to', e.target.value)} className="h-8 text-sm" />
                                  {addr.duration_to && <span className="text-xs text-slate-500">{formatDateForDisplay(addr.duration_to)}</span>}
                                </div>
                                <div>
                                  <Label className="text-xs">Period of Stay</Label>
                                  <Input value={addr.period_of_stay} onChange={(e) => updateAdditionalAddress(index, 'period_of_stay', e.target.value)} placeholder="e.g., 2 years" className="h-8 text-sm" />
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}

                    {/* Add Additional Address Button */}
                    <Button
                      variant="outline"
                      onClick={addAdditionalAddress}
                      className="w-full mt-4 border-dashed border-slate-300 hover:border-slate-400"
                      data-testid="add-address-button-qc"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Add Additional Address (Previous / Native Place / Other)
                    </Button>
                  </TabsContent>

                  {/* ID Details Tab */}
                  <TabsContent value="id" className="p-4 max-h-[450px] overflow-y-auto">
                    <Card className="border-slate-200">
                      <CardHeader className="py-3">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <CreditCard className="w-4 h-4" />
                          Identity Documents
                        </CardTitle>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <div className="space-y-6">
                          {/* PAN Card */}
                          <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                              <Label className="text-xs">PAN Number</Label>
                              <Input 
                                value={idData.pan_number} 
                                onChange={(e) => setIdData(prev => ({ ...prev, pan_number: e.target.value.toUpperCase() }))} 
                                placeholder="ABCDE1234F"
                                maxLength={10}
                              />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Upload PAN Card (PDF/JPG) *</Label>
                              <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idData.pan_card_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleIdDocUpload('pan_card', e.target.files[0])}
                                  className="hidden"
                                  id="pan-upload"
                                />
                                <label htmlFor="pan-upload" className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${idData.pan_card_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {idData.pan_card_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                  {idData.pan_card_file || 'Choose File'}
                                </label>
                              </div>
                            </div>
                          </div>
                          
                          {/* Aadhar Card */}
                          <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                              <Label className="text-xs">Aadhar Number</Label>
                              <Input 
                                value={idData.aadhar_number} 
                                onChange={(e) => setIdData(prev => ({ ...prev, aadhar_number: e.target.value }))} 
                                placeholder="1234 5678 9012"
                                maxLength={14}
                              />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Upload Aadhar Card (PDF/JPG) *</Label>
                              <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idData.aadhar_card_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleIdDocUpload('aadhar_card', e.target.files[0])}
                                  className="hidden"
                                  id="aadhar-upload"
                                />
                                <label htmlFor="aadhar-upload" className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${idData.aadhar_card_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {idData.aadhar_card_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                  {idData.aadhar_card_file || 'Choose File'}
                                </label>
                              </div>
                            </div>
                          </div>
                          
                          {/* Driving License */}
                          <div className="grid grid-cols-2 gap-4 pb-4 border-b">
                            <div>
                              <Label className="text-xs">Driving License Number</Label>
                              <Input 
                                value={idData.dl_number} 
                                onChange={(e) => setIdData(prev => ({ ...prev, dl_number: e.target.value }))} 
                                placeholder="KA01 2020 0001234"
                              />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Upload Driving License (PDF/JPG)</Label>
                              <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idData.driving_license_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleIdDocUpload('driving_license', e.target.files[0])}
                                  className="hidden"
                                  id="dl-upload"
                                />
                                <label htmlFor="dl-upload" className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${idData.driving_license_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {idData.driving_license_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                  {idData.driving_license_file || 'Choose File'}
                                </label>
                              </div>
                            </div>
                          </div>
                          
                          {/* Passport */}
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <Label className="text-xs">Passport Number</Label>
                              <Input 
                                value={idData.passport_number} 
                                onChange={(e) => setIdData(prev => ({ ...prev, passport_number: e.target.value.toUpperCase() }))} 
                                placeholder="A1234567"
                                maxLength={8}
                              />
                            </div>
                            <div>
                              <Label className="text-xs mb-1 block">Upload Passport (PDF/JPG)</Label>
                              <div className={`border-2 border-dashed rounded-lg p-3 text-center cursor-pointer transition-colors ${idData.passport_file ? 'border-emerald-400 bg-emerald-50' : 'border-slate-200 hover:border-purple-300'}`}>
                                <input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => handleIdDocUpload('passport', e.target.files[0])}
                                  className="hidden"
                                  id="passport-upload"
                                />
                                <label htmlFor="passport-upload" className={`cursor-pointer flex items-center justify-center gap-2 text-sm ${idData.passport_file ? 'text-emerald-600' : 'text-slate-500'}`}>
                                  {idData.passport_file ? <CheckCircle className="w-4 h-4" /> : <Upload className="w-4 h-4" />}
                                  {idData.passport_file || 'Choose File'}
                                </label>
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </TabsContent>

                  {/* References Tab */}
                  <TabsContent value="references" className="p-4 max-h-[450px] overflow-y-auto">
                    <div className="space-y-4">
                      {/* Professional References */}
                      <Card className="border-slate-200">
                        <CardHeader className="py-3 flex flex-row items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Briefcase className="w-4 h-4" />
                            Professional References
                          </CardTitle>
                          <Button onClick={() => addReference('professional')} size="sm" variant="outline">
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {referencesData.professional.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">No professional references added</p>
                          ) : (
                            <div className="space-y-3">
                              {referencesData.professional.map((ref, idx) => (
                                <div key={idx} className="p-3 bg-slate-50 rounded-lg relative">
                                  <Button variant="ghost" size="sm" className="absolute top-1 right-1 text-rose-600" onClick={() => removeReference('professional', idx)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  <div className="grid grid-cols-4 gap-2">
                                    <div>
                                      <Label className="text-xs">Name</Label>
                                      <Input value={ref.name || ''} onChange={(e) => updateReference('professional', idx, 'name', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Relationship</Label>
                                      <Input value={ref.relationship || ''} onChange={(e) => updateReference('professional', idx, 'relationship', e.target.value)} placeholder="Manager, Colleague" />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Organization</Label>
                                      <Input value={ref.organization || ''} onChange={(e) => updateReference('professional', idx, 'organization', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Designation</Label>
                                      <Input value={ref.designation || ''} onChange={(e) => updateReference('professional', idx, 'designation', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Phone</Label>
                                      <Input 
                                        value={ref.phone || ''} 
                                        onChange={(e) => updateReference('professional', idx, 'phone', formatPhoneMax10(e.target.value))} 
                                        maxLength={10}
                                        className={ref.phone && ref.phone.length !== 10 ? 'border-red-400' : ''}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Email</Label>
                                      <Input type="email" value={ref.email || ''} onChange={(e) => updateReference('professional', idx, 'email', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Known Since</Label>
                                      <Input value={ref.known_since || ''} onChange={(e) => updateReference('professional', idx, 'known_since', e.target.value)} placeholder="2 years" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Personal References */}
                      <Card className="border-slate-200">
                        <CardHeader className="py-3 flex flex-row items-center justify-between">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Personal References
                          </CardTitle>
                          <Button onClick={() => addReference('personal')} size="sm" variant="outline">
                            <Plus className="w-4 h-4 mr-1" />
                            Add
                          </Button>
                        </CardHeader>
                        <CardContent className="pt-0">
                          {referencesData.personal.length === 0 ? (
                            <p className="text-sm text-slate-400 text-center py-4">No personal references added</p>
                          ) : (
                            <div className="space-y-3">
                              {referencesData.personal.map((ref, idx) => (
                                <div key={idx} className="p-3 bg-slate-50 rounded-lg relative">
                                  <Button variant="ghost" size="sm" className="absolute top-1 right-1 text-rose-600" onClick={() => removeReference('personal', idx)}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                  <div className="grid grid-cols-4 gap-2">
                                    <div>
                                      <Label className="text-xs">Name</Label>
                                      <Input value={ref.name || ''} onChange={(e) => updateReference('personal', idx, 'name', e.target.value)} />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Relationship</Label>
                                      <Input value={ref.relationship || ''} onChange={(e) => updateReference('personal', idx, 'relationship', e.target.value)} placeholder="Friend, Neighbor" />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Phone</Label>
                                      <Input 
                                        value={ref.phone || ''} 
                                        onChange={(e) => updateReference('personal', idx, 'phone', formatPhoneMax10(e.target.value))} 
                                        maxLength={10}
                                        className={ref.phone && ref.phone.length !== 10 ? 'border-red-400' : ''}
                                      />
                                    </div>
                                    <div>
                                      <Label className="text-xs">Known Since</Label>
                                      <Input value={ref.known_since || ''} onChange={(e) => updateReference('personal', idx, 'known_since', e.target.value)} placeholder="5 years" />
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </TabsContent>

                  {/* Review & Submit Tab */}
                  <TabsContent value="review" className="p-4 max-h-[450px] overflow-y-auto">
                    <Card className="border-slate-200 shadow-sm overflow-hidden">
                      <CardHeader className="bg-gradient-to-r from-slate-700 to-slate-800 text-white py-3">
                        <CardTitle className="text-white flex items-center gap-2">
                          <CheckCircle className="w-5 h-5" />
                          Review & Submit
                        </CardTitle>
                        <p className="text-sm text-white/80">Review verification requirements and submit details</p>
                      </CardHeader>
                      <CardContent className="space-y-6 p-4">
                        {/* Package Info */}
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                          <h4 className="font-semibold text-blue-900 mb-1">
                            Verification Package: {selectedCase?.entity_name || selectedCase?.client_name || 'Case'} - {selectedCase?.id}
                          </h4>
                          <p className="text-sm text-blue-700">The following checks will be performed based on verification requirements</p>
                        </div>

                        {/* Verification Checks Required */}
                        <div className="space-y-4">
                          <h4 className="font-semibold text-slate-900">Verification Checks Required</h4>
                          {(() => {
                            const checks = selectedCase?.checks || [];
                            if (checks.length === 0) {
                              return (
                                <div className="p-4 bg-slate-50 rounded-lg text-center text-slate-500">
                                  No verification checks configured for this case
                                </div>
                              );
                            }

                            // Group checks by type and sum quantities
                            const groupedChecks = {};
                            checks.forEach(check => {
                              let checkType = 'unknown';
                              let quantity = 1;
                              if (typeof check === 'string') {
                                checkType = check;
                              } else if (typeof check === 'object') {
                                checkType = check.check_type || check.type || 'unknown';
                                if (typeof checkType === 'object') checkType = checkType.id || checkType.type || checkType.check_type || 'unknown';
                                quantity = check.quantity || 1;
                              }
                              if (checkType === 'court_advocate_check') checkType = 'criminal_advocate_check';
                              if (groupedChecks[checkType]) {
                                groupedChecks[checkType].quantity += quantity;
                              } else {
                                groupedChecks[checkType] = { checkType, quantity };
                              }
                            });

                            const uniqueChecks = Object.values(groupedChecks);

                            return (
                              <div className="grid gap-3">
                                {uniqueChecks.map((checkData, idx) => {
                                  const checkType = String(checkData.checkType);
                                  const quantity = checkData.quantity;

                                  const checkInfo = {
                                    'id_check': { name: 'ID Verification', icon: CreditCard, section: 'id' },
                                    'employment_check': { name: 'Employment Verification', icon: Briefcase, section: 'employment' },
                                    'education_check': { name: 'Education Verification', icon: GraduationCap, section: 'education' },
                                    'address_check': { name: 'Address Verification', icon: MapPin, section: 'address' },
                                    'professional_reference': { name: 'Professional Reference Check', icon: Users, section: 'references' },
                                    'personal_reference': { name: 'Personal Reference Check', icon: Users, section: 'references' },
                                    'reference_check': { name: 'Reference Check', icon: Users, section: 'references' },
                                    'criminal_court_check': { name: 'Criminal Court Check', icon: FileText, section: 'address' },
                                    'criminal_advocate_check': { name: 'Criminal Advocate Check', icon: FileText, section: 'address' },
                                    'world_criminal_db': { name: 'World Database Check', icon: FileText, section: 'basic' },
                                    'india_criminal_db': { name: 'India Criminal Database Check', icon: FileText, section: 'basic' },
                                    'global_database': { name: 'Global Database Check', icon: FileText, section: 'basic' },
                                    'credit_check': { name: 'Credit Check', icon: CreditCard, section: 'id' },
                                    'drug_test': { name: 'Drug Test', icon: FileText, section: 'basic' }
                                  };

                                  const info = checkInfo[checkType] || {
                                    name: checkType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                                    icon: FileText,
                                    section: null
                                  };
                                  const Icon = info.icon;

                                  // Count filled entries
                                  let filledCount = 0;
                                  let autoFilledSource = '';

                                  if (checkType === 'employment_check') {
                                    filledCount = employmentEntries.filter(e => (e.employer_name || e.company_name || '').trim()).length;
                                  } else if (checkType === 'education_check') {
                                    filledCount = educationEntries.filter(e => (e.course || '').trim()).length;
                                  } else if (checkType === 'address_check') {
                                    const presentFilled = (addressData.present.house_no || addressData.present.area) ? 1 : 0;
                                    const permanentFilled = (addressData.permanent.house_no || addressData.permanent.area) ? 1 : 0;
                                    const additionalFilled = additionalAddresses.filter(a => (a.house_no || a.area || '').trim()).length;
                                    filledCount = presentFilled + permanentFilled + additionalFilled;
                                  } else if (checkType === 'professional_reference') {
                                    filledCount = (referencesData.professional || []).filter(r => (r.name || '').trim()).length;
                                  } else if (checkType === 'personal_reference' || checkType === 'reference_check') {
                                    filledCount = (referencesData.personal || []).filter(r => (r.name || '').trim()).length;
                                  } else if (checkType === 'id_check') {
                                    filledCount = (idData.pan_number ? 1 : 0) + (idData.aadhar_number ? 1 : 0);
                                    autoFilledSource = 'Copies: Basic Details + PAN/Aadhaar';
                                  } else if (checkType === 'criminal_court_check' || checkType === 'criminal_advocate_check') {
                                    const presentFilled = (addressData.present.house_no || addressData.present.area) ? 1 : 0;
                                    const permanentFilled = (addressData.permanent.house_no || addressData.permanent.area) ? 1 : 0;
                                    const additionalFilled = additionalAddresses.filter(a => (a.house_no || a.area || '').trim()).length;
                                    const totalAddr = presentFilled + permanentFilled + additionalFilled;
                                    filledCount = presentFilled ? Math.min(quantity, totalAddr) : 0;
                                    autoFilledSource = 'Copies: Present Address + Subsequent Addresses';
                                  } else if (['world_criminal_db', 'india_criminal_db', 'global_database'].includes(checkType)) {
                                    filledCount = (employeeData?.name && employeeData?.dob && employeeData?.father_name) ? quantity : 0;
                                    autoFilledSource = 'Copies: Basic Details (Name, DOB, Father Name)';
                                  } else if (checkType === 'credit_check') {
                                    filledCount = (employeeData?.name && idData.pan_number) ? quantity : 0;
                                    autoFilledSource = 'Copies: Basic Details + PAN Card';
                                  } else if (checkType === 'drug_test') {
                                    filledCount = employeeData?.name ? quantity : 0;
                                    autoFilledSource = 'Copies: Basic Details';
                                  }

                                  const isComplete = filledCount >= quantity;
                                  // Per-check-type declaration for fewer records
                                  const canDeclareFewer = !isComplete && filledCount > 0 && (
                                    checkType === 'employment_check' ||
                                    checkType === 'education_check' ||
                                    checkType === 'address_check'
                                  );
                                  const declareState = checkType === 'employment_check' ? employmentDeclaration
                                    : checkType === 'education_check' ? educationDeclaration
                                    : checkType === 'address_check' ? addressDeclaration
                                    : false;
                                  const setDeclareState = checkType === 'employment_check' ? setEmploymentDeclaration
                                    : checkType === 'education_check' ? setEducationDeclaration
                                    : checkType === 'address_check' ? setAddressDeclaration
                                    : null;
                                  const declareLabelNoun = checkType === 'employment_check' ? 'employment record(s)'
                                    : checkType === 'education_check' ? 'education record(s)'
                                    : 'address(es)';

                                  return (
                                    <div key={idx} className={`p-4 rounded-lg border ${isComplete || (canDeclareFewer && declareState) ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'}`}
                                      data-testid={`review-check-${checkType}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                          <Icon className={`w-5 h-5 ${isComplete || (canDeclareFewer && declareState) ? 'text-emerald-600' : 'text-amber-600'}`} />
                                          <div>
                                            <p className={`font-medium ${isComplete || (canDeclareFewer && declareState) ? 'text-emerald-900' : 'text-amber-900'}`}>
                                              {info.name}
                                            </p>
                                            <p className={`text-sm ${isComplete || (canDeclareFewer && declareState) ? 'text-emerald-700' : 'text-amber-700'}`}>
                                              Required: {quantity} | Filled: {filledCount}
                                              {autoFilledSource && <span className="ml-2 text-xs italic">({autoFilledSource})</span>}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                          {isComplete || (canDeclareFewer && declareState) ? (
                                            <Badge className="bg-emerald-600 text-white">
                                              <CheckCircle className="w-3 h-3 mr-1" />
                                              Complete
                                            </Badge>
                                          ) : (
                                            <Badge className="bg-amber-600 text-white">
                                              <AlertTriangle className="w-3 h-3 mr-1" />
                                              Incomplete
                                            </Badge>
                                          )}
                                          {info.section && (
                                            <Button
                                              size="sm"
                                              variant="outline"
                                              onClick={() => setActiveTab(info.section)}
                                              className="text-xs"
                                            >
                                              Edit
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                      {canDeclareFewer && setDeclareState && (
                                        <label className="mt-3 flex items-start gap-2 cursor-pointer pt-3 border-t border-amber-200">
                                          <input
                                            type="checkbox"
                                            checked={declareState}
                                            onChange={(e) => setDeclareState(e.target.checked)}
                                            className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-600 focus:ring-amber-500"
                                            data-testid={`declare-fewer-${checkType}`}
                                          />
                                          <span className="text-sm text-amber-900">
                                            I declare that the candidate has only <strong>{filledCount}</strong> {declareLabelNoun} to verify. I understand that fewer records will be verified and the missing slots will be marked as <em>N/A by declaration</em>.
                                          </span>
                                        </label>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })()}
                        </div>

                        {/* Submit Button */}
                        {(['initiated', 'basic_initiated'].includes(selectedCase?.status)) && (
                          <div className="pt-4 border-t">
                            <Button
                              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => setShowSubmitDialog(true)}
                              data-testid="review-submit-btn"
                            >
                              <Send className="w-4 h-4 mr-2" />
                              Submit to QC for Review
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </>
          )}
        </Card>
      </div>

      {/* Submit Confirmation Dialog */}
      <Dialog open={showSubmitDialog} onOpenChange={setShowSubmitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Case to QC</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600 mb-3">
              You are about to submit the case for <strong>{employeeData?.name}</strong> ({selectedCase?.id}) to Pending QC review.
            </p>
            <p className="text-sm text-slate-500">
              This will save all current details and move the case from <strong>Draft</strong> to <strong>Pending QC</strong> status. 
              Please ensure all check details and documents are complete before submitting.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-700" 
              onClick={handleSubmitCase}
              disabled={submitting}
              data-testid="confirm-submit-case-btn"
            >
              {submitting ? 'Submitting...' : 'Confirm Submit to QC'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
